const { mainConfigService, googlePayConfigService } = require('./services/config');
const { SDK_FOPS } = require('./variables');
const { sdkFetch } = require('./http');
const {
  validateWalletButtonType,
  validateWalletButtonColor,
  validateWalletButtonSizeMode,
  validateWalletButtonLocale,
} = require('./util');

let callbackService;
let threeDSService;
let validatePayment;
let oneLastCheckGate;

function initializeGooglePayServices(cbService, _threeDSService, _validatePayment, _oneLastCheckGate) {
  callbackService = cbService;
  threeDSService = _threeDSService;
  validatePayment = _validatePayment;
  oneLastCheckGate = _oneLastCheckGate;
}

function baseRequest() {
  return {
    apiVersion: googlePayConfigService.get('apiVersion')
      ? googlePayConfigService.get('apiVersion')
      : 2,
    apiVersionMinor: googlePayConfigService.get('apiVersionMinor')
      ? googlePayConfigService.get('apiVersionMinor')
      : 0,
  };
}

const allowedCardNetworks = [
  'AMEX',
  'DISCOVER',
  'INTERAC',
  'JCB',
  'MASTERCARD',
  'VISA',
];
const allowedCardAuthMethods = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];

function tokenizationSpecification() {
  return {
    type: googlePayConfigService.get('tokenizationSpecificationType')
      ? googlePayConfigService.get('tokenizationSpecificationType')
      : 'PAYMENT_GATEWAY',
    parameters:
      googlePayConfigService.get('tokenizationSpecificationType') === 'DIRECT'
        ? {
          protocolVersion: 'ECv2',
          publicKey: googlePayConfigService.get('publicKey')
            ? googlePayConfigService.get('publicKey')
            : undefined,
        }
        : {
          gateway: googlePayConfigService.get('gateway')
            ? googlePayConfigService.get('gateway')
            : undefined,
          gatewayMerchantId: googlePayConfigService.get('gateway_merchant_id')
            ? googlePayConfigService.get('gateway_merchant_id')
            : undefined,
        },
  };
}

function baseCardPaymentMethod() {
  return {
    type: 'CARD',
    parameters: {
      allowedAuthMethods: googlePayConfigService.get('allowedCardAuthMethods')
        ? googlePayConfigService.get('allowedCardAuthMethods')
        : allowedCardAuthMethods,
      allowedCardNetworks: googlePayConfigService.get('allowedCardNetworks')
        ? googlePayConfigService.get('allowedCardNetworks')
        : allowedCardNetworks,
      allowPrepaidCards: googlePayConfigService.get('allowPrepaidCards')
        ? googlePayConfigService.get('allowPrepaidCards')
        : true,
      allowCreditCards: googlePayConfigService.get('allowCreditCards')
        ? googlePayConfigService.get('allowCreditCards')
        : true,
      assuranceDetailsRequired: googlePayConfigService.get('assuranceDetailsRequired')
        ? googlePayConfigService.get('assuranceDetailsRequired')
        : false,
      billingAddressRequired: googlePayConfigService.get('billingAddressRequired')
        ? googlePayConfigService.get('billingAddressRequired')
        : false,
      billingAddressParameters: googlePayConfigService.get('billingAddressParameters')
        ? googlePayConfigService.get('billingAddressParameters')
        : undefined,
    },
  };
}

function cardPaymentMethod() {
  return {
    ...baseCardPaymentMethod(),
    tokenizationSpecification: tokenizationSpecification(),
  };
}

let paymentsClient = null;

function getGoogleIsReadyToPayRequest() {
  return {
    ...baseRequest(),
    allowedPaymentMethods: [baseCardPaymentMethod()],
  };
}

function getGooglePaymentDataRequest() {
  const paymentDataRequest = { ...baseRequest() };
  paymentDataRequest.allowedPaymentMethods = [cardPaymentMethod()];
  paymentDataRequest.transactionInfo = getGoogleTransactionInfo();
  paymentDataRequest.emailRequired = googlePayConfigService.get('emailRequired')
    ? googlePayConfigService.get('emailRequired')
    : false;
  paymentDataRequest.merchantInfo = {
    merchantId: googlePayConfigService.get('merchant_id')
      ? googlePayConfigService.get('merchant_id')
      : undefined,
    merchantName: googlePayConfigService.get('merchant_name')
      ? googlePayConfigService.get('merchant_name')
      : undefined,
  };
  paymentDataRequest.callbackIntents = ['PAYMENT_AUTHORIZATION'];

  return paymentDataRequest;
}

function getGooglePaymentsClient() {
  if (paymentsClient === null) {
    paymentsClient = new google.payments.api.PaymentsClient({
      environment: googlePayConfigService.get('environment')
        ? googlePayConfigService.get('environment')
        : undefined,
      paymentDataCallbacks: {
        onPaymentAuthorized: onPaymentAuthorized,
      },
    });
  }
  return paymentsClient;
}

function onPaymentAuthorized(paymentData) {
  return new Promise(function (resolve) {
    callbackService.callBackPayment()
      .then(() => {
        processPayment(paymentData, function (outcome) {
          if (outcome.status === '3DS') {
            resolve({ transactionState: 'SUCCESS' });
            threeDSService.generate3DSPopUp(outcome);
          } else if (outcome.approved) {
            callbackService.callBackSuccess(outcome.callback_payload);
            resolve({ transactionState: 'SUCCESS' });
          } else if (!outcome.approved) {
            if (
              outcome.callback_payload &&
              outcome.callback_payload.status == 'canceled'
            ) {
              resolve({
                transactionState: 'ERROR',
                error: {
                  intent: 'PAYMENT_AUTHORIZATION',
                  message: outcome.callback_payload.message || outcome.message,
                  reason: 'OTHER_ERROR',
                },
              });
              callbackService.callBackCancel(outcome.callback_payload);
            } else if (
              outcome.callback_payload &&
              outcome.callback_payload.status == 'error'
            ) {
              resolve({
                transactionState: 'ERROR',
                error: {
                  intent: 'PAYMENT_AUTHORIZATION',
                  message: outcome.callback_payload.message || outcome.message,
                  reason: 'OTHER_ERROR',
                },
              });
              callbackService.callBackError(outcome.callback_payload);
            }
          } else {
            resolve({
              transactionState: 'ERROR',
              error: {
                intent: 'PAYMENT_AUTHORIZATION',
                message: outcome.message || 'Failed to Fetch',
                reason: 'OTHER_ERROR',
              },
            });
            callbackService.callBackError({
              status: 'error',
              form_of_payment: SDK_FOPS.GOOGLE_PAY,
              message: outcome.message || 'Failed to Fetch',
              ...outcome,
            });
          }
        });
      })
      .catch(() => {
        resolve({ transactionState: 'SUCCESS' });
      });
  });
}

async function onGooglePayLoaded(documentCheckout) {
  const paymentsClient = getGooglePaymentsClient();
  return await paymentsClient
    .isReadyToPay(getGoogleIsReadyToPayRequest())
    .then(async function (response) {
      if (response.result) {
        return await addGooglePayButton(documentCheckout);
      }
    })
    .catch(function (err) {
      callbackService.callBackError({
        status: 'error',
        form_of_payment: SDK_FOPS.GOOGLE_PAY,
        message: err.message,
        ...err,
      });
    });
}

async function addGooglePayButton(documentCheckout) {
  const paymentsClient = getGooglePaymentsClient();
  try {
    const button = paymentsClient.createButton({
      buttonColor:
        googlePayConfigService.get('theme')?.buttonColor &&
        validateWalletButtonColor(
          googlePayConfigService.get('theme').buttonColor,
          'googlePay'
        )
          ? googlePayConfigService.get('theme').buttonColor
          : 'default',
      buttonType:
        googlePayConfigService.get('theme')?.buttonType &&
        validateWalletButtonType(
          googlePayConfigService.get('theme').buttonType,
          'googlePay'
        )
          ? googlePayConfigService.get('theme').buttonType
          : 'plain',
      buttonLocale:
        googlePayConfigService.get('buttonLocale') &&
        validateWalletButtonLocale(googlePayConfigService.get('buttonLocale'), 'googlePay')
          ? googlePayConfigService.get('buttonLocale')
          : 'en',
      buttonSizeMode:
        googlePayConfigService.get('theme')?.buttonSizeMode &&
        validateWalletButtonSizeMode(
          googlePayConfigService.get('theme').buttonSizeMode,
          'googlePay'
        )
          ? googlePayConfigService.get('theme').buttonSizeMode
          : 'fill',
      onClick: onGooglePaymentButtonClicked,
      buttonRootNode: documentCheckout,
    });
    return button;
  } catch (err) {
    callbackService.callBackError({
      status: 'error',
      form_of_payment: SDK_FOPS.GOOGLE_PAY,
      message: err.message,
      ...err,
    });
  }
}

function getGoogleTransactionInfo() {
  let fee_description = undefined;
  if (googlePayConfigService.get('fee') > 0) {
    fee_description = {
      label: googlePayConfigService.get('fee_description') || 'Fee',
      type: 'LINE_ITEM',
      price: `${parseFloat(googlePayConfigService.get('fee'))}`,
      status: 'FINAL',
    };
  }
  return {
    countryCode: googlePayConfigService.get('country_code')
      ? googlePayConfigService.get('country_code')
      : undefined,
    currencyCode: googlePayConfigService.get('currency_code')
      ? googlePayConfigService.get('currency_code')
      : undefined,
    totalPriceStatus: googlePayConfigService.get('totalPriceStatus')
      ? googlePayConfigService.get('totalPriceStatus')
      : 'FINAL',
    totalPrice: googlePayConfigService.get('total_price')
      ? `${parseFloat(googlePayConfigService.get('total_price'))}`
      : undefined,
    totalPriceLabel: googlePayConfigService.get('totalPriceLabel')
      ? googlePayConfigService.get('totalPriceLabel')
      : 'Total',
    displayItems:
      fee_description &&
      googlePayConfigService.get('displayItems') &&
      googlePayConfigService.get('displayItems').length
        ? [fee_description, ...googlePayConfigService.get('displayItems')]
        : fee_description
          ? [fee_description]
          : undefined,
  };
}

function onGooglePaymentButtonClicked() {
  validatePayment()
    .then(async (isValid)=>{
      if (isValid === false) {
        return; // stop execution if the validate promise rejects;
      }
      if (oneLastCheckGate) {
        const proceed = await oneLastCheckGate();
        if (!proceed) return;
      }
      const paymentDataRequest = getGooglePaymentDataRequest();
      const paymentsClient = getGooglePaymentsClient();

      paymentsClient.loadPaymentData(paymentDataRequest).catch((err) => {
        if (err.statusCode === 'CANCELED') {
          return; // Skip calling errorCallback when user cancels the sheet
        }
        callbackService.callBackError({
          status: 'error',
          form_of_payment: SDK_FOPS.GOOGLE_PAY,
          message:
            err.message ||
            err.statusMessage ||
            'User closed the Payment Request UI.',
          ...err,
        });
      });
    });
    
}

function processPayment(paymentData, callback) {
  const body = {
    google_pay_payload: {
      apiVersion: paymentData.apiVersion,
      apiVersionMinor: paymentData.apiVersionMinor,
      paymentMethodData: paymentData.paymentMethodData,
    },
    currency_code: googlePayConfigService.get('currency_code'),
    amount: googlePayConfigService.get('total_price'),
    session_id: mainConfigService.get('session_id'),
    code: googlePayConfigService.get('code'),
  };

  sdkFetch(googlePayConfigService.get('payment_url'), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Api-Key ${mainConfigService.get('apiKey')}`,
    },
    body: JSON.stringify(body),
  })
    .then(async (response) => {
      if (response.ok) {
        return await response.json();
      } else {
        throw new Error(JSON.stringify(await response.json()));
      }
    })
    .then((data) => {
      callback(data);
    })
    .catch((err) => {
      callback(JSON.parse(err.message));
    });
}

exports.initializeGooglePayServices = initializeGooglePayServices;
exports.onGooglePayLoaded = onGooglePayLoaded;
exports.processPayment = processPayment;
